import type { SocketClosed } from '../types'

/**
 * Five attempts, doubling, ceiling eight seconds.
 *
 * Deliberately shorter than the SSE transport's 30s ceiling: that one exists to
 * stay inside the server's replay window, and the agent has no replay. What
 * governs here is a person sitting at a table wondering if it is broken.
 */
const DELAYS_MS = [1000, 2000, 4000, 8000, 8000]

/** WebSocket policy violation: a malformed or unauthorised URL. */
const POLICY_VIOLATION = 1008
/** Normal closure. Deliberate on both sides, so the reason decides. */
const NORMAL = 1000

/**
 * How long to wait before trying again, or null to stop for good.
 *
 * `attempt` is how many reconnects have already been tried, so it is 0 on the
 * first drop.
 *
 * 1008 never retries. It means the agent refused the request itself — today
 * that is a missing property_id, and when signed QR tokens land a rejected
 * token will arrive the same way. Neither gets better by asking again.
 *
 * **A clean close carrying a reason never retries either.** That is the server
 * hanging up on purpose — the 10-minute idle timeout today. Dialling back would
 * reopen the paid pipeline it just ended and greet a customer who has left.
 * Matching on "a reason is present" rather than on the string means the next
 * deliberate hangup the agent adds is handled without editing this file.
 *
 * A bare 1000 with no reason still retries: in practice that is the process
 * restarting, and reconnecting is exactly what the customer wants.
 */
export function reconnectDelayMs({ code, reason }: SocketClosed, attempt: number): number | null {
  if (code === POLICY_VIOLATION) return null
  if (code === NORMAL && reason.trim()) return null
  return DELAYS_MS[attempt] ?? null
}
